import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { friendService } from '../../../services';
import FriendActionButton from '../../FriendActionButton';
import { SearchIcon } from '../../hand/icons';
import { tintFor } from '../../../utils/tints';
import { readDismissedPeople } from '../../PeopleYouMayKnow';

/**
 * BRING PEOPLE IN — round-3 handoff §2e. One white panel: a search pill that both filters
 * the rail and searches, then a horizontal rail of people you may know (72px photo, name,
 * `N MUTUAL`, + ADD → SENT ✓). Two or more characters run the real people search and the
 * results take the rail's place as rows with FriendActionButton, close-friend mark included.
 */
const profilePath = (p) => (p.handle ? `/${p.handle}` : `/users/${p.userId}`);

export default function BringPeopleIn({ suggestions = [], loading = false, onSent }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [sent, setSent] = useState(() => new Set());

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults(null); return undefined; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      const r = await friendService.searchPeople(term);
      if (cancelled) return;
      setResults(r.success ? (r.data || []) : []);
      setSearching(false);
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  const add = async (person) => {
    if (sent.has(person.userId)) return;
    const r = await friendService.sendFriendRequest(person.userId);
    if (!r.success) { onSent?.(null, r); return; }
    setSent((prev) => new Set(prev).add(person.userId));
    onSent?.(person);
  };

  const hidden = readDismissedPeople();
  const term = q.trim().toLowerCase();
  const rail = suggestions
    .filter((p) => !hidden.has(p.userId))
    .filter((p) => !term || `${p.displayName} ${p.handle || ''}`.toLowerCase().includes(term));

  return (
    <section className="hd-people" aria-labelledby="hd-people-h">
      <div className="hd-people-head">
        <h2 className="hd-h2" id="hd-people-h">BRING PEOPLE IN</h2>
        <small>PEOPLE YOU MAY KNOW</small>
        <span className="hd-rule" />
        <button type="button" onClick={() => navigate('/people?tab=suggested')}>ALL →</button>
      </div>
      <div className="hd-people-panel">
        <label className="hd-search">
          <SearchIcon size={16} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or handle…"
            aria-label="Search people"
            autoComplete="off"
            data-1p-ignore="true"
          />
        </label>

        {results ? (
          <div className="hd-results" data-testid="people-results">
            {searching && results.length === 0 && <span className="hd-panel-empty">SEARCHING…</span>}
            {!searching && results.length === 0 && <span className="hd-panel-empty">NO ONE BY THAT NAME.</span>}
            {results.map((p) => (
              <div key={p.userId} className="hd-result">
                <button type="button" className="hd-result-who" onClick={() => navigate(profilePath(p))}>
                  <span style={{ background: tintFor(p.userId) }}>
                    {p.avatarUrl ? <img src={p.avatarUrl} alt="" referrerPolicy="no-referrer" /> : (p.displayName || '?')[0]}
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <b>{p.displayName}</b>
                    {p.handle && <small>@{p.handle}</small>}
                  </span>
                </button>
                <FriendActionButton
                  status={p.relationshipStatus}
                  requestSent={sent.has(p.userId) || p.relationshipStatus === 'request_sent'}
                  onAdd={() => add(p)}
                  onRespond={() => navigate('/friends')}
                  userId={p.userId}
                  isClose={p.isClose}
                  name={p.displayName}
                />
              </div>
            ))}
          </div>
        ) : loading ? (
          <span className="hd-panel-empty">FINDING PEOPLE…</span>
        ) : rail.length === 0 ? (
          <span className="hd-panel-empty">{term ? 'NO ONE BY THAT NAME.' : 'NO SUGGESTIONS YET.'}</span>
        ) : (
          <div className="hd-rail" data-testid="people-rail">
            {rail.map((p) => {
              const isSent = sent.has(p.userId) || p.relationshipStatus === 'request_sent';
              return (
                <div key={p.userId} className="hd-person">
                  <button type="button" className="hd-person-photo" style={{ background: tintFor(p.userId) }} onClick={() => navigate(profilePath(p))} aria-label={p.displayName}>
                    {p.avatarUrl ? <img src={p.avatarUrl} alt="" referrerPolicy="no-referrer" /> : (p.displayName || '?')[0]}
                  </button>
                  <b>{p.displayName}</b>
                  <small>{p.mutualCount || 0} MUTUAL</small>
                  <button type="button" className="hd-add" data-sent={isSent ? '' : undefined} onClick={() => add(p)} disabled={isSent}>
                    {isSent ? 'SENT ✓' : '+ ADD'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
